import React, { useMemo, useContext, useCallback } from 'react';
import classNames from 'classnames';
import Animation from 'components/Animation';
import { Button, Loader, Modal } from 'ncoded-component-library';
import { ExtractPropTypes } from 'types';
import { useTranslation } from 'react-i18next';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { Option } from 'models/Global';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import SelectField from 'components/SelectField';
import bus, { StandardEventTypes } from 'modules/bus';
import { Field, withTypes } from 'react-final-form';
import { UserConnection } from 'models/Message';
import validations from 'validations';
import api from 'api';
import MessagingContext from '../../../providers/Messaging/Messaging.context';
import useMatchMedia from 'hooks/useMatchMedia';
import { getUserFullNameOrEmail } from 'utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { DEFAULT_LIMIT } from 'data/global';

import './NewMessageModal.styles.scss';

const SEPARATOR = ' - ';

type NewMessageFormType = {
  sessionId: string;
};

type NewMessageModalProps = {
  className?: string;
  onSend?: () => void;
} & ExtractPropTypes<typeof Modal>;

const NewMessageModal: React.FC<NewMessageModalProps> = (props) => {
  const { className, open, onClose } = props;

  const { popServerError } = useContext(PopNotificationsContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { listingId, sessionId, setContactOpen, setContactsOpen } =
    useContext(MessagingContext);

  const { t } = useTranslation();
  const isPhablet = useMatchMedia('isPhablet');

  const { Form } = withTypes<NewMessageFormType>();

  const classes = classNames('new-message-modal', className);

  const {
    loading: loadingConnections,
    items: connections,
    setSearchString,
    onContainerScrolled: onSellersScroll,
  } = useInfinitePagination<UserConnection>({
    condition: open,
    resetDeps: [open],
    dependencies: [open],
    onResetDeps: () => setSearchString(''),
    makeRequest: async (currentPage: number, searchString: string) => {
      return api.messages
        .getUserConnections({
          $page: currentPage.toString(),
          $limit: DEFAULT_LIMIT,
          $sort: '-createdAt',
          $searchString: searchString,
          listingId,
        })
        .then((res) => res.data);
    },
  });

  const doesntHaveOptions = !loadingConnections && !connections?.length;

  const mappedConnections = useMemo(
    () =>
      connections
        ?.map((connection) => {
          const { interlocutor, isBuyer, listing, session } = connection;

          const { isDirectMessage } = session || {};

          if (isDirectMessage) {
            return {
              isDirectMessage,
              value: session.id,
              interlocutorName: getUserFullNameOrEmail(interlocutor),
              dealName: '',
            };
          }

          const { dealRoom } = listing || {};
          const { name: dealName, companyName } = dealRoom || {};

          const baseName = isBuyer
            ? companyName
            : getUserFullNameOrEmail(interlocutor);

          const interlocutorName = baseName ? baseName + SEPARATOR : '';

          return {
            value: session.id,
            interlocutorName,
            dealName,
          };
        })
        .sort((a, b) => {
          const aHasInterlocutor = !!a.interlocutorName;
          const bHasInterlocutor = !!b.interlocutorName;

          if (aHasInterlocutor && !bHasInterlocutor) return -1;
          if (!aHasInterlocutor && bHasInterlocutor) return 1;

          if (aHasInterlocutor && bHasInterlocutor) {
            return a.interlocutorName.localeCompare(b.interlocutorName);
          }

          return a.dealName.localeCompare(b.dealName);
        })
        .map(({ value, isDirectMessage, interlocutorName, dealName }) => {
          return {
            value,
            label: isDirectMessage
              ? interlocutorName
              : [interlocutorName, dealName].filter(Boolean).join(' '),
          } as Option;
        }),
    [connections],
  );

  const onSubmit = useCallback(
    async (values: NewMessageFormType) => {
      try {
        const { sessionId: sessionIdVal } = values;

        // if current session is same as picked one, don't update it
        if (sessionId !== sessionIdVal) {
          const connection = connections.find(
            (conn) => conn.session.id === sessionIdVal,
          );

          bus.broadcastEvent(StandardEventTypes.BULK_SESSION, [
            {
              ...connection.session,
              isBuyer: connection.isBuyer,
              interlocutor: connection.interlocutor,
            },
          ]);
        }

        if (isPhablet) {
          setContactOpen(false);
          setContactsOpen(false);
        }
      } catch (e) {
        popServerError(e);
      } finally {
        onClose();
      }
    },
    [
      isPhablet,
      sessionId,
      connections,
      onClose,
      popServerError,
      setContactOpen,
      setContactsOpen,
    ],
  );

  return (
    <Animation
      open={open}
      duration={200}
      appearAnimation="appear"
      disappearAnimation="disappear"
      timingFunction="ease-in-out"
    >
      <Modal
        open
        className={classes}
        title={t('composeNewMessage')}
        onOverlayClose={onClose}
        onX={onClose}
        onClose={onClose}
      >
        <Form onSubmit={onSubmit}>
          {({ handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <p>
                {t(
                  doesntHaveOptions
                    ? 'noRecipientsToSelectFrom'
                    : isSuperAdmin
                    ? 'composeNewMessageDescriptionBM'
                    : 'composeNewMessageDescription',
                )}
              </p>

              <div className="select-wrapper">
                {loadingConnections && <Loader overlay={false} />}

                <Field
                  name="sessionId"
                  async
                  options={mappedConnections}
                  loading={loadingConnections}
                  component={SelectField}
                  searchable
                  placeholder={t('chooseRecipient')}
                  onScroll={onSellersScroll}
                  validate={validations.required()}
                  onSearchChange={(event: any) => {
                    setSearchString(event.target.value);
                  }}
                />
              </div>

              <div className="actions">
                <Button variant="link" onClick={onClose}>
                  {t('cancel')}
                </Button>
                <Button type="submit">{t('confirm')}</Button>
              </div>
            </form>
          )}
        </Form>
      </Modal>
    </Animation>
  );
};

export default NewMessageModal;
